import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import logodark from "../../assets/images/susi-pizza-new-logo.png";
import { LoginUserapi } from '../../requests';
import { useNavigate } from "react-router-dom";
import { Form, Input, Label } from "reactstrap";
import LodingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import withRouter from "../../components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginSuccess } from "../../store/actions";
import { createSelector } from 'reselect';
import "./Login.css";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = "Admin Login | Susipizza";
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please enter your username or email"),
      password: Yup.string().required("Please enter your password"),
    }),
    onSubmit: async (values) => {
      try {
        await loginDirect(values);
      } catch (error) {
        console.log(error);
      }
    },
  });

  const loginDirect = async (values) => {
    try {
      setLoading(true);
      const response = await LoginUserapi(values);
      const { token, role, storeId, storeName } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      if (storeId) {
        localStorage.setItem('storeId', storeId);
      }
      if (storeName) {
        localStorage.setItem('storeName', storeName);
      }

      dispatch(loginSuccess(values.username, role));
      navigate('/dashboard');
      toast.success("Login Successful");
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const loginpage = createSelector(
    (state) => state.login,
    (state) => ({
      error: state.error,
    })
  );
  const { error } = useSelector(loginpage);

  return (
    <React.Fragment>
      <LodingSpinner loading={loading} />
      <div className="admin-auth-page">
        <div className="admin-auth-card">
          {/* Left Hero/Brand Section */}
          <div className="admin-auth-brand-side">
            <div>
              <div className="brand-header-badge">
                <i className="mdi mdi-shield-check-outline"></i>
                <span>Official Admin Portal</span>
              </div>

              <div className="brand-hero-content">
                <h1 className="brand-hero-title">
                  Manage Your <span>Pizza Store</span> Operations
                </h1>
                <p className="brand-hero-desc">
                  Streamline orders, manage menus, track riders, and analyze business performance from one central hub.
                </p>
              </div>

              <div className="brand-feature-list">
                <div className="brand-feature-item">
                  <div className="brand-feature-icon">
                    <i className="mdi mdi-pizza"></i>
                  </div>
                  <div className="brand-feature-text">
                    <h6>Live Order Dispatch</h6>
                    <p>Track incoming kitchen orders and status updates instantly</p>
                  </div>
                </div>

                <div className="brand-feature-item">
                  <div className="brand-feature-icon">
                    <i className="mdi mdi-motorbike"></i>
                  </div>
                  <div className="brand-feature-text">
                    <h6>Fleet & Rider Management</h6>
                    <p>Assign drivers and monitor active delivery routes</p>
                  </div>
                </div>

                <div className="brand-feature-item">
                  <div className="brand-feature-icon">
                    <i className="mdi mdi-chart-timeline-variant"></i>
                  </div>
                  <div className="brand-feature-text">
                    <h6>Analytics & Insights</h6>
                    <p>Comprehensive store analytics and daily sales reports</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="brand-footer-text">
              &copy; {new Date().getFullYear()} Susipizza. Crafted with <i className="mdi mdi-heart text-danger"></i> by Crework
            </p>
          </div>

          {/* Right Form Section */}
          <div className="admin-auth-form-side">
            <div className="form-header">
              <Link to="/" className="form-logo-container">
                <img src={logodark} alt="Susipizza Admin Logo" className="form-logo-img" />
              </Link>
              <h2 className="form-header-title">Welcome Back !</h2>
              <p className="form-header-sub">Sign in to continue to Susipizza Admin</p>
            </div>

            <Form
              className="form-horizontal"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              {error ? (
                <div className="admin-error-banner">
                  <i className="mdi mdi-alert-circle-outline fs-5"></i>
                  <div>{error}</div>
                </div>
              ) : null}

              <div className="admin-form-group">
                <Label className="admin-form-label">Username</Label>
                <div className="admin-input-wrapper">
                  <i className="mdi mdi-account-outline admin-input-icon"></i>
                  <Input
                    name="username"
                    className="admin-form-input"
                    placeholder="Enter Username"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.username || ""}
                    invalid={validation.touched.username && validation.errors.username ? true : false}
                  />
                </div>
                {validation.touched.username && validation.errors.username ? (
                  <span className="admin-field-error">{validation.errors.username}</span>
                ) : null}
              </div>

              <div className="admin-form-group">
                <Label className="admin-form-label">Password</Label>
                <div className="admin-input-wrapper">
                  <i className="mdi mdi-lock-outline admin-input-icon"></i>
                  <Input
                    name="password"
                    className="admin-form-input"
                    value={validation.values.password || ""}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter Password"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.password && validation.errors.password ? true : false}
                  />
                  <button
                    type="button"
                    className="admin-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`mdi ${showPassword ? "mdi-eye-off-outline" : "mdi-eye-outline"}`}></i>
                  </button>
                </div>
                {validation.touched.password && validation.errors.password ? (
                  <span className="admin-field-error">{validation.errors.password}</span>
                ) : null}
              </div>

              <button
                className="admin-submit-btn"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <span>Authenticating...</span>
                ) : (
                  <>
                    <span>Log In</span>
                    <i className="mdi mdi-arrow-right fs-5"></i>
                  </>
                )}
              </button>
            </Form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);

Login.propTypes = {
  history: PropTypes.object,
};
